// lint-internal-leaks-ignore-next-line: framework-script — load-bearing visual regression marker
// @visual-diff-render: vite
/**
 * @file AssessmentScale — rating-scale picker
 * @version 0.2.3
 */

import React, { forwardRef, type ElementType, type HTMLAttributes } from 'react';
import { AssessmentCard, type AssessmentRank } from '../AssessmentCard';
import styles from './AssessmentScale.module.css';

/*
 * AssessmentScale — rating-scale picker. 2-tier responsive primitive [sm · md].
 *
 *   md (≥ 768): horizontal row of 5 full AssessmentCards (number + title +
 *     description per card).
 *   sm (< 768): chip panel — 5 number-only pill chips wrapped in a chassis,
 *     with a details slot below showing either endpoint labels (no pick)
 *     OR the selected card's title + description.
 *
 *   <AssessmentScale items={SCALE_ITEMS} selected={3} onSelect={setPick} />
 *
 * Detection: JS matchMedia (Pattern B) — the layout flips conditionally
 * render compact-vs-full cards plus chassis chrome, which @media alone can't
 * express cleanly. Caller can pin a tier with `forceLayout` for tests,
 * popups, and visual-regression sidecars.
 */

export interface AssessmentScaleItem {
  number: AssessmentRank;
  title: string;
  description: string;
}

export type AssessmentScaleLayout = 'sm' | 'md';

/* Native `HTMLAttributes` has its own DOM `onSelect` (text-selection event), which conflicts with this component's rank-picker `onSelect(rank)` callback. Omit the native one and kept the component's own. */
export interface AssessmentScaleProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onSelect'> {
  /** The five scale items rendered as cards, left → right. */
  items: readonly AssessmentScaleItem[];
  /** Currently picked rank (1–5). Maps to the card's `selected` visual. */
  selected?: AssessmentRank;
  /** Click handler. Receives the picked rank. Omit for a reference render. */
  onSelect?: (rank: AssessmentRank) => void;
  /**
   * Pin the layout tier (skips matchMedia auto-detect).
   *   - `sm` = chip-panel layout (viewport < 768)
   *   - `md` = full-card row layout (viewport ≥ 768)
   */
  forceLayout?: AssessmentScaleLayout;
  /**
   * Explicit width override. Without this the scale fills its column
   * (`100%`). Regression sidecars pin a specific Figma source width
   * (e.g. 343 for sm, 1000 for md) so screenshot baselines are
   * deterministic.
   */
  width?: number | string;
  /**
   * Colour treatment forwarded to each AssessmentCard. `dark` (default) is the
   * frosted-glass card; `light` is the light-surface card. Per-instance variant.
   */
  theme?: 'light' | 'dark';
  /** Render the root as a different element/component. Default `'div'`. */
  as?: ElementType;
}

export const AssessmentScale = forwardRef<HTMLDivElement, AssessmentScaleProps>(
  function AssessmentScale(
    { items, selected, onSelect, forceLayout, width = '100%', theme = 'dark', as, className, style, ...rest },
    ref,
  ) {
  const Component = as ?? 'div';
  const [isBelow, setIsBelow] = React.useState<boolean>(forceLayout === 'sm');

  React.useEffect(() => {
    if (forceLayout) {
      setIsBelow(forceLayout === 'sm');
      return;
    }
    if (typeof window === 'undefined') return;
    // Figma source-of-truth: AssessmentScale switches to chip-panel below
    // 768 (the assessment screens' `size: "767&bellow"` variant).
    const mq = window.matchMedia('(max-width: 767px)');
    const update = () => setIsBelow(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, [forceLayout]);

  const rootClass = [styles.scale, styles[theme], className].filter(Boolean).join(' ');
  const selectedItem =
    selected !== undefined ? items.find((it) => it.number === selected) : undefined;
  const first = items[0];
  const last = items[items.length - 1];

  // Radio-group keyboard pattern (mirrors AssessmentQuestionStacked): the scale
  // is ONE tab stop (roving tabindex) and Left/Right/Up/Down + Home/End move and
  // select between the cards, wrapping at both ends. Refs let the handler move
  // focus to the newly-picked card.
  const optionRefs = React.useRef<(HTMLButtonElement | null)[]>([]);
  const count = items.length;
  const selectedIndex =
    selected !== undefined ? items.findIndex((it) => it.number === selected) : -1;
  const tabbableIndex = selectedIndex >= 0 ? selectedIndex : 0;

  const pick = (index: number) => {
    if (!onSelect) return;
    const clamped = ((index % count) + count) % count; // wrap both ends
    onSelect(items[clamped].number);
    optionRefs.current[clamped]?.focus();
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        pick(index + 1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        pick(index - 1);
        break;
      case 'Home':
        e.preventDefault();
        pick(0);
        break;
      case 'End':
        e.preventDefault();
        pick(count - 1);
        break;
      default:
        break;
    }
  };

  return (
    <Component
      ref={ref}
      className={rootClass}
      role="radiogroup"
      data-layout={isBelow ? 'sm' : 'md'}
      data-state={selectedItem ? 'picked' : 'empty'}
      style={{ width, ...style }}
      {...rest}
    >
      <div className={styles.cards}>
        {items.map((item, index) => {
          const isSel = selected === item.number;
          // Light sm: number pills (default outlined / hover teal / selected
          // navy) that fill the full-width card — a 40px circle at the narrowest,
          // stretching into a pill as the card widens. Not the frosted compact
          // card. Dark sm + md keep the AssessmentCard rendering unchanged.
          if (isBelow && theme === 'light') {
            return (
              <button
                key={item.number}
                ref={(el) => {
                  optionRefs.current[index] = el;
                }}
                type="button"
                role="radio"
                aria-checked={isSel}
                aria-label={`${item.number}. ${item.title}`}
                tabIndex={index === tabbableIndex ? 0 : -1}
                onKeyDown={(e) => handleKeyDown(e, index)}
                onClick={onSelect ? () => onSelect(item.number) : undefined}
                className={styles.dot}
                data-selected={isSel || undefined}
              >
                {item.number}
              </button>
            );
          }
          return (
            <AssessmentCard
              key={item.number}
              ref={(el) => {
                optionRefs.current[index] = el;
              }}
              radio
              tabIndex={index === tabbableIndex ? 0 : -1}
              onKeyDown={(e) => handleKeyDown(e, index)}
              number={item.number}
              title={item.title}
              description={item.description}
              selected={isSel}
              compact={isBelow}
              theme={theme}
              onClick={onSelect ? () => onSelect(item.number) : undefined}
            />
          );
        })}
      </div>

      {isBelow && (
        <div className={styles.details}>
          {selectedItem ? (
            <>
              <span className={styles.detailsTitle}>{selectedItem.title}</span>
              <span className={styles.detailsDesc}>{selectedItem.description}</span>
            </>
          ) : (
            <div className={styles.endpoints}>
              <span className={styles.endpointStart}>{first?.title}</span>
              <span className={styles.endpointEnd}>{last?.title}</span>
            </div>
          )}
        </div>
      )}
    </Component>
  );
  },
);

AssessmentScale.displayName = 'AssessmentScale';
